import React, { useState } from "react";
import axios from "axios";
import { Phone, Clock, Send, Loader2, PhoneCall } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { PHONE_DISPLAY, PHONE_TEL, BUSINESS_TYPES } from "../../data/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast({ title: "Please add your name and work email.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        business_type: businessType || "",
      });
      setSubmitted(name.trim().split(" ")[0]);
    } catch (err) {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-cream grain px-5 md:px-8 py-20 lg:py-28">
      <div className="max-w-[1240px] mx-auto">
        <div className="bg-navy rounded-[32px] p-8 md:p-14 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-lime">
              07 <span className="text-cream/40">—</span> The next move
            </p>
            <h2 className="mt-5 font-grotesk font-bold text-cream leading-[1.0] tracking-tight text-[clamp(2.25rem,4.5vw,3.75rem)]">
              Don't let the next good customer become another missed call.
            </h2>
            <p className="mt-6 text-cream/70 text-lg leading-relaxed max-w-md">
              Tell us a little about your business. We'll show you where CallRetrieve can create coverage first.
            </p>
            <div className="mt-8 space-y-3">
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2.5 text-lime font-grotesk font-semibold text-xl">
                <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
              </a>
              <p className="flex items-center gap-2 font-mono text-[13px] text-cream/50">
                <Clock className="w-4 h-4" /> Setup conversations are 20 minutes
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-7 md:p-9 shadow-2xl">
            {submitted ? (
              <div className="py-6 text-center fade-up">
                <div className="mx-auto w-14 h-14 rounded-full bg-lime flex items-center justify-center">
                  <PhoneCall className="w-6 h-6 text-navy" />
                </div>
                <h3 className="mt-5 font-grotesk text-2xl font-bold text-navy">
                  {`You're on the list, ${submitted}.`}
                </h3>
                <p className="mt-2 text-navy/60">
                  Thanks for reaching out. We’ll follow up with a clear next step for your business.
                </p>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="mt-6 inline-flex items-center gap-2.5 bg-navy text-cream font-grotesk font-semibold px-6 py-3.5 rounded-full hover:bg-navy-800 transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call us now
                </a>
              </div>
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h3 className="font-grotesk text-2xl font-bold text-navy">Start the conversation</h3>
                  <span className="text-[12px] text-navy/45">Usually replies within one business day.</span>
                </div>

                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-navy/50">Your name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Jane Smith"
                      className="mt-2 w-full h-12 px-4 bg-cream rounded-xl border border-navy/10 text-navy placeholder:text-navy/35 focus:outline-none focus:ring-2 focus:ring-teal/40"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-navy/50">Work email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="jane@yourbusiness.com"
                      className="mt-2 w-full h-12 px-4 bg-cream rounded-xl border border-navy/10 text-navy placeholder:text-navy/35 focus:outline-none focus:ring-2 focus:ring-teal/40"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-navy/50">Business type</label>
                    <Select value={businessType} onValueChange={setBusinessType}>
                      <SelectTrigger className="mt-2 h-12 bg-cream border-navy/10 rounded-xl text-navy">
                        <SelectValue placeholder="Select one" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-13 py-3.5 inline-flex items-center justify-center gap-2.5 bg-lime text-navy font-grotesk font-semibold rounded-full hover:bg-lime-bright transition-colors disabled:opacity-70"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Get my coverage plan
                  </button>
                  <p className="text-center text-[12px] text-navy/45">
                    By submitting, you agree to be contacted about CallRetrieve.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
